import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { RelatoriosApi } from './relatorios.api.js';
import { logger } from '../../core/logger.js';

/**
 * Card "Site e tráfego (Google Analytics)" — compartilhado pelo Dashboard CEO (admin) e pelo
 * Dashboard operacional (financeiro etc.). Uma única implementação: HTML, CSS e carga ficam aqui;
 * cada página só injeta `CSS_SITE_TRAFEGO` no `<style>`, `HTML_CARD_SITE_TRAFEGO` no layout e
 * chama `ligarCardSiteTrafego(root, api)`.
 *
 * Dados: `GET /relatorios/analytics-site` (GA4 do site + pedidos PAGOS do ERP, cache 15 min no
 * backend). Indisponível/erro aparece como está, nunca número inventado.
 */

export const CSS_SITE_TRAFEGO = `
  .site-dias { display: flex; gap: 4px; }
  .site-dias-btn { background: transparent; border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); color: var(--pg-color-texto-secundario, #9aa1ab); border-radius: 8px; padding: 3px 9px; font-size: 12px; cursor: pointer; }
  .site-dias-btn.is-ativo { color: var(--pg-color-texto-primario, #f2f3f5); border-color: var(--pg-color-destaque, #d4af37); }
  .site-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: var(--pg-space-3, 12px); margin-bottom: var(--pg-space-4, 16px); }
  .site-kpi { border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); border-radius: 12px; padding: 10px 12px; min-width: 0; }
  .site-kpi small { display: block; font-size: 12px; color: var(--pg-color-texto-secundario, #9aa1ab); }
  .site-kpi b { display: block; font-size: 1.35rem; font-variant-numeric: tabular-nums; color: var(--pg-color-texto-primario, #f2f3f5); margin-top: 2px; overflow-wrap: anywhere; }
  .site-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: var(--pg-space-4, 16px); }
  .site-bloco { min-width: 0; }
  .site-bloco h4 { margin: 0 0 8px; font-size: 12px; letter-spacing: .04em; text-transform: uppercase; color: var(--pg-color-texto-secundario, #9aa1ab); font-weight: 600; }
  .site-linha { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); padding: 4px 0; }
  .site-barra { grid-column: 1 / -1; height: 4px; border-radius: 4px; background: var(--pg-color-borda, #2a2f36); overflow: hidden; }
  .site-barra i { display: block; height: 100%; background: var(--pg-color-destaque, #d4af37); }
  .site-nota { margin-top: var(--pg-space-3, 12px); font-size: 12px; color: var(--pg-color-texto-secundario, #9aa1ab); }
  .site-trafego-card { margin-top: var(--pg-space-4, 16px); }
`;

export const HTML_CARD_SITE_TRAFEGO = `
  <pg-card class="secao site-trafego-card">
    <span slot="header">Site e tráfego (Google Analytics)</span>
    <div slot="header-acao" class="site-dias">
      <button type="button" data-dias="7" class="site-dias-btn is-ativo">7 dias</button>
      <button type="button" data-dias="30" class="site-dias-btn">30 dias</button>
    </div>
    <div id="site-trafego-body"><pg-empty-state variant="carregando" titulo="Site e tráfego"></pg-empty-state></div>
  </pg-card>
`;

function esc(valor: string): string {
  return valor.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Liga os botões 7/30 dias e faz a primeira carga. */
export function ligarCardSiteTrafego(root: ShadowRoot | HTMLElement, api: RelatoriosApi | null): void {
  let dias: 7 | 30 = 7;
  root.querySelectorAll<HTMLButtonElement>('.site-dias-btn').forEach((botao) => {
    botao.addEventListener('click', () => {
      const novo = botao.dataset['dias'] === '30' ? 30 : 7;
      if (novo === dias) return;
      dias = novo;
      root.querySelectorAll('.site-dias-btn').forEach((b) => b.classList.toggle('is-ativo', b === botao));
      void carregarCardSiteTrafego(root, api, dias);
    });
  });
  void carregarCardSiteTrafego(root, api, dias);
}

/** Marca o card como "sem permissão" (papel sem relatorios:ler). */
export function cardSiteTrafegoSemPermissao(root: ShadowRoot | HTMLElement): void {
  const body = root.querySelector('#site-trafego-body');
  if (body) body.innerHTML = '<pg-empty-state variant="sem-permissao" titulo="Site e tráfego"></pg-empty-state>';
}

export async function carregarCardSiteTrafego(root: ShadowRoot | HTMLElement, api: RelatoriosApi | null, dias: 7 | 30): Promise<void> {
  const body = root.querySelector('#site-trafego-body');
  if (!api || !body) return;
  body.innerHTML = '<pg-empty-state variant="carregando" titulo="Site e tráfego"></pg-empty-state>';
  try {
    const resultado = await api.obterAnalyticsSite(dias);
    if (resultado.status === 'sem-permissao') return cardSiteTrafegoSemPermissao(root);
    if (resultado.status !== 'ok') {
      const msg = resultado.status === 'erro' ? resultado.mensagem : 'indisponível';
      body.innerHTML = `<pg-empty-state variant="erro" titulo="Site e tráfego" descricao="${esc(msg)}"></pg-empty-state>`;
      return;
    }
    const a = resultado.dado;
    if (!a.disponivel || !a.visitas) {
      body.innerHTML = `<pg-empty-state variant="indisponivel" titulo="Site e tráfego" descricao="${esc(a.motivo ?? 'indisponível')}"></pg-empty-state>`;
      return;
    }
    const n = (v: number): string => v.toLocaleString('pt-BR');
    const linhas = (itens: Array<{ rotulo: string; valor: number }>): string => {
      const max = Math.max(1, ...itens.map((i) => i.valor));
      return itens
        .map(
          (i) => `<div class="site-linha"><span>${esc(i.rotulo)}</span><b>${n(i.valor)}</b>
            <span class="site-barra"><i style="width:${Math.round((i.valor / max) * 100)}%"></i></span></div>`
        )
        .join('');
    };
    const pedidos = a.pedidosPagosErp ?? { quantidade: 0, valorCentavos: 0 };
    const conversao = a.conversaoPercentual == null ? 'sem dados' : `${a.conversaoPercentual.toLocaleString('pt-BR')}%`;
    const origens = (a.origens ?? []).map((o) => ({ rotulo: o.canal, valor: o.sessoes }));
    body.innerHTML = `
      <div class="site-kpis">
        <div class="site-kpi"><small>Visitas</small><b>${n(a.visitas.sessoes)}</b></div>
        <div class="site-kpi"><small>Visitantes</small><b>${n(a.visitas.usuarios)}</b></div>
        <div class="site-kpi"><small>Pedidos pagos (ERP)</small><b>${n(pedidos.quantidade)}</b></div>
        <div class="site-kpi"><small>Faturado no período</small><b>${esc(formatBRL(centavosParaReais(pedidos.valorCentavos)))}</b></div>
        <div class="site-kpi"><small>Conversão (pedidos ÷ visitas)</small><b>${esc(conversao)}</b></div>
      </div>
      <div class="site-grid">
        <div class="site-bloco"><h4>De onde vieram as visitas</h4>${origens.length ? linhas(origens) : '<div class="site-nota">sem visitas no período</div>'}</div>
        <div class="site-bloco"><h4>Funil de compra no site</h4>${linhas((a.funil ?? []).map((f) => ({ rotulo: f.rotulo, valor: f.quantidade })))}</div>
        <div class="site-bloco"><h4>Atendimento</h4>${linhas([
          { rotulo: 'Abriram o chat Fale com a PG', valor: a.atendimento?.chatAberto ?? 0 },
          { rotulo: 'Foram para o WhatsApp pelo chat', valor: a.atendimento?.whatsapp ?? 0 },
          { rotulo: 'Outros contatos (WhatsApp, telefone, Instagram)', valor: a.atendimento?.contatos ?? 0 },
          { rotulo: 'Geraram orçamento em PDF', valor: a.atendimento?.orcamentos ?? 0 },
        ])}</div>
      </div>
      <div class="site-nota">Últimos ${a.dias} dias. Visitas e funil vêm do Google Analytics do site; pedidos e faturamento são os pagos reais do ERP. Atualiza a cada 15 minutos.</div>`;
  } catch (erro) {
    logger.erro('falha ao carregar site e tráfego', { erro });
    body.innerHTML = '<pg-empty-state variant="erro" titulo="Site e tráfego" descricao="falha ao carregar"></pg-empty-state>';
  }
}
